import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Input from "../../components/form/input/InputField";
import { vehicleTypeLabel } from "../../utils/vehicleTypeLabel";

interface VehicleTimePrice {
  id: string;
  vehicleTypeId: string;
  fromTime: string;
  toTime: string;
  fromTimeString: string;
  toTimeString: string;
  priceAdjustPers: number;
  status: boolean;
}

interface SortConfig {
  key: keyof VehicleTimePrice | '';
  direction: 'asc' | 'desc';
}

interface VehicleType {
  id: string;
  name?: string | null;
  description: string;
}

export default function VehicleTimePrices() {
  const [timePrices, setTimePrices] = useState<VehicleTimePrice[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<Record<string, VehicleType>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });

  useEffect(() => {
    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTimePrices();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage, pageSize, sortConfig]);

  const fetchVehicleTypes = async () => {
    try {
      const response = await axiosInstance.get("/vehicletypes");
      const types = response.data.reduce((acc: Record<string, VehicleType>, type: VehicleType) => {
        acc[type.id] = type;
        return acc;
      }, {});
      setVehicleTypes(types);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
    }
  };

  const fetchTimePrices = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/vehicletimeprices");
      const allPrices = response.data;
      
      const filteredPrices = allPrices.filter((price: VehicleTimePrice) => {
        const vehicleType = vehicleTypeLabel(
          vehicleTypes[price.vehicleTypeId] ?? { description: "" },
        ).toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        return vehicleType.includes(searchLower) ||
               price.fromTimeString?.toLowerCase().includes(searchLower) ||
               price.toTimeString?.toLowerCase().includes(searchLower);
      });

      if (sortConfig.key !== '') {
        filteredPrices.sort((a: VehicleTimePrice, b: VehicleTimePrice) => {
          if (sortConfig.key === 'fromTimeString' || sortConfig.key === 'toTimeString') {
            // Convert times to minutes for proper sorting
            const getMinutes = (timeStr: string) => {
              const [time, period] = timeStr.split(' ');
              const [hours, minutes] = time.split(':').map(Number);
              let totalMinutes = hours * 60 + minutes;
              if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
              if (period === 'AM' && hours === 12) totalMinutes = minutes;
              return totalMinutes;
            };

            const aTime = getMinutes(a[sortConfig.key] || '');
            const bTime = getMinutes(b[sortConfig.key] || '');
            return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
          }

          const aValue = String(a[sortConfig.key as keyof VehicleTimePrice]);
          const bValue = String(b[sortConfig.key as keyof VehicleTimePrice]);
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      const totalItems = filteredPrices.length;
      const calculatedTotalPages = Math.ceil(totalItems / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      setTotalPages(calculatedTotalPages);
      setTimePrices(filteredPrices.slice(startIndex, endIndex));
    } catch (error) {
      setError("Failed to fetch time prices");
      console.error("Error fetching time prices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this time price?")) {
      try {
        await axiosInstance.delete(`/vehicletimeprices/${id}`);
        fetchTimePrices();
      } catch (error) {
        setError("Failed to delete time price");
        console.error("Error deleting time price:", error);
      }
    }
  };

  const handleSort = (key: keyof VehicleTimePrice) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i
              ? "rounded-lg bg-brand-500 text-white"
              : "rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
          }`}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  const renderSortIcon = (key: keyof VehicleTimePrice) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Vehicle Time Prices"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to="/vehicle-time-prices/add"
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Add Time Price
        </Link>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="p-4 border-b">
          <Input
            placeholder="Search time prices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {error && (
          <div className="border-b border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('vehicleTypeId')}
                >
                  Vehicle Type {renderSortIcon('vehicleTypeId')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('fromTimeString')}
                >
                  From Time {renderSortIcon('fromTimeString')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('toTimeString')}
                >
                  To Time {renderSortIcon('toTimeString')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('priceAdjustPers')}
                >
                  Price Adjustment {renderSortIcon('priceAdjustPers')}
                </th>
                <th 
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  Status {renderSortIcon('status')}
                </th>
                <th className="px-5 py-3 text-end text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                    </div>
                  </td>
                </tr>
              ) : timePrices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No time prices found
                  </td>
                </tr>
              ) : (
                timePrices.map((price) => (
                  <tr key={price.id}>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {vehicleTypes[price.vehicleTypeId]
                        ? vehicleTypeLabel(vehicleTypes[price.vehicleTypeId])
                        : "N/A"}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {price.fromTimeString}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {price.toTimeString}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {price.priceAdjustPers}%
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        price.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {price.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-end">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                        to={`/vehicle-time-prices/detail/${price.id}`}
                        className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
                      >
                        View
                      </Link>
                      <Link
                        to={`/vehicle-time-prices/edit/${price.id}`}
                        className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => price.id && handleDelete(price.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && timePrices.length > 0 && (
          <div className="px-6 py-4 border-t">
            <div className="flex justify-between items-center">
              <div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 